import Marquee from "react-fast-marquee";
import { CalendarDays, Building2, MapPin, ArrowRight, Route } from "lucide-react";
import { Reveal } from "./Reveal";
import { EARNINGS_EXAMPLES } from "@/data/content";

const MARQUEE_WORDS = ["TRANSPARENZ", "VERDIENST", "MÖGLICHKEITEN", "EHRLICHKEIT"];

export const Earnings = () => {
  return (
    <section id="verdienst" data-testid="earnings" className="relative overflow-hidden py-24 sm:py-32">
      {/* Editorial marquee background */}
      <div className="pointer-events-none absolute inset-0 -z-10 flex flex-col justify-center gap-2 opacity-[0.05]">
        <Marquee speed={40} autoFill>
          <span className="mx-6 font-heading text-[7rem] font-extrabold uppercase leading-none tracking-tight text-white sm:text-[10rem]">
            {MARQUEE_WORDS.join(" • ")} •{" "}
          </span>
        </Marquee>
        <Marquee speed={40} direction="right" autoFill>
          <span className="mx-6 font-heading text-[7rem] font-extrabold uppercase leading-none tracking-tight text-white sm:text-[10rem]">
            {MARQUEE_WORDS.join(" • ")} •{" "}
          </span>
        </Marquee>
      </div>

      <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
        <Reveal>
          <span className="font-heading text-xs font-semibold uppercase tracking-[0.24em] text-brand-blue">
            Was kann man damit verdienen?
          </span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-[3.4rem]">
            Lohnt sich Fahrzeug­überführung <span className="text-gradient-brand">überhaupt?</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mx-auto mt-7 max-w-2xl space-y-4 font-body text-base leading-relaxed text-white/70 sm:text-lg">
            <p>Genau diese Frage stellen sich viele Einsteiger.</p>
            <p className="text-white/90">
              Die ehrliche Antwort: <span className="font-semibold text-brand-green">Es kommt auf deine Aufträge an.</span>
            </p>
            <p>
              Vergütung, Entfernung, Anreise, Rückfahrt und dein Zeitaufwand spielen eine
              entscheidende Rolle. Deshalb geht es nicht einfach darum, möglichst viele Aufträge
              anzunehmen – sondern zu verstehen, welche Touren sich tatsächlich rechnen.
            </p>
            <p>
              Im Webinar zeige ich anhand konkreter Beispiele, wie du einen Auftrag kalkulierst und
              worauf du achten solltest.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-12">
            <p className="mb-6 font-heading text-xs font-semibold uppercase tracking-[0.2em] text-white/45">
              Beispiel-Aufträge · zur Veranschaulichung
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {EARNINGS_EXAMPLES.map((ex, i) => (
                <div
                  key={i}
                  data-testid={`earnings-example-${i}`}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-brand-surface text-left transition-colors duration-300 hover:border-brand-green/40"
                >
                  <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.04] px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5 font-heading text-[11px] font-semibold uppercase tracking-wide text-brand-green">
                      <Route className="h-3.5 w-3.5" /> Überführungsauftrag
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-body text-xs text-white/55">
                      <CalendarDays className="h-3.5 w-3.5" /> {ex.date}
                    </span>
                  </div>
                  <div className="space-y-4 p-5">
                    <div className="flex items-center gap-2 font-body text-sm text-white/70">
                      <Building2 className="h-4 w-4 shrink-0 text-brand-blue" /> {ex.client}
                    </div>
                    <div className="flex items-center justify-between gap-2 rounded-xl bg-black/30 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 shrink-0 text-brand-green" />
                        <span className="font-heading text-sm font-bold text-white">{ex.from}</span>
                      </div>
                      <div className="flex flex-col items-center text-white/45">
                        <span className="font-body text-[10px] font-semibold">{ex.km} km</span>
                        <ArrowRight className="h-4 w-4" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-sm font-bold text-white">{ex.to}</span>
                        <MapPin className="h-4 w-4 shrink-0 text-brand-magenta" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/10 pt-4">
                      <span className="font-body text-xs text-white/50">Vergütung</span>
                      <span className="font-heading text-2xl font-extrabold text-brand-green">{ex.pay} €</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-8 max-w-xl rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4 font-body text-xs leading-relaxed text-white/40">
            Die gezeigten Beispiele stellen keine Einkommensgarantie dar. Der tatsächliche Verdienst
            hängt unter anderem von verfügbaren Aufträgen, Zeitaufwand, Kosten und der persönlichen
            Auftragsauswahl ab.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
